/**
 * @module CollectionReceiptsService
 * @purpose API service for collection receipts, hash verification, and chain of custody
 * @depends apiClient, tokenStorage from ./api; collection-receipts types
 * @dependedOnBy useReceiptMutations, UploadReceiptModal, CollectionReceiptsList
 *
 * @rules
 * - Performance: XHR used for upload to expose progress events unavailable via fetch
 * - State: stateless service layer; all state managed in hooks
 * - Errors: ApiError propagated to hooks; uploadReceipt XHR errors wrapped as UploadReceiptError
 *   (preserves the backend's error code alongside the message)
 */

import { apiClient, tokenStorage } from './api'
import type {
  CollectionReceipt,
  ChainOfCustodyEntry,
  ReceiptStatistics,
  HashVerificationResult,
  RecordChainOfCustodyInput,
  UpdateHashVerificationInput,
  UpdateIngestionStatusInput,
  UpdateReceiptNotesInput,
  ListReceiptsParams,
  ListChainOfCustodyParams,
  ReceiptsListResponse,
  ChainOfCustodyListResponse,
} from '../types/collection-receipts'

// =============================================================================
// UPLOAD SIZE CAP
// =============================================================================

const BYTES_PER_KIB = 1024
const KIB_PER_MIB = 1024
const MIB_PER_GIB = 1024
const DEFAULT_MAX_UPLOAD_GIB = 2
const DEFAULT_MAX_UPLOAD_BYTES = DEFAULT_MAX_UPLOAD_GIB * MIB_PER_GIB * KIB_PER_MIB * BYTES_PER_KIB

const envUploadBytes: string | undefined = import.meta.env.VITE_MAX_UPLOAD_BYTES as
  | string
  | undefined

/**
 * Maximum upload size in bytes.
 *
 * @behaviour Reads VITE_MAX_UPLOAD_BYTES build-time constant; falls back to 2 GiB.
 * @errors Deploy config must set VITE_MAX_UPLOAD_BYTES to match EVIDENX_MAX_UPLOAD_BYTES.
 */
export const MAX_UPLOAD_BYTES: number = envUploadBytes
  ? Number.parseInt(envUploadBytes, 10)
  : DEFAULT_MAX_UPLOAD_BYTES

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Build query string from params object
 *
 * @behaviour Skips null and undefined values; returns empty string when no params
 * @errors None — pure synchronous transformation
 */
function buildQueryString(params: object): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value))
    }
  }
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

// =============================================================================
// COLLECTION RECEIPT API
// =============================================================================

/**
 * List receipts for a collection request
 */
export async function listReceipts(
  caseId: string,
  requestId: string,
  params?: ListReceiptsParams
): Promise<ReceiptsListResponse> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<ReceiptsListResponse>(
    `/cases/${caseId}/collection-requests/${requestId}/receipts${query}`
  )
}

/**
 * Get receipt statistics for a collection request
 */
export async function getReceiptStatistics(
  caseId: string,
  requestId: string
): Promise<ReceiptStatistics> {
  return apiClient.get<ReceiptStatistics>(
    `/cases/${caseId}/collection-requests/${requestId}/receipts/statistics`
  )
}

/**
 * Metadata accompanying a receipt upload.
 *
 * @behaviour All camelCase fields map to the backend's UploadReceiptMetadata via serde rename_all camelCase.
 * @errors collectionTargetId, receivedFromName, receivedMethod are required by the backend handler.
 */
export interface UploadReceiptMetadata {
  collectionTargetId: string
  receivedFromName: string
  receivedFromRole?: string
  receivedMethod: 'in_person' | 'courier' | 'email' | 'sftp' | 'other'
  receivedMethodDetails?: string
  velociraptorVersion?: string
  collectorHashSha256?: string
  imagingHashSha256?: string
  encryptionScheme?: 'none' | 'password' | 'x509' | 'pgp'
  oobZipSha256?: string
  notes?: string
  /**
   * Analyst has seen the duplicate-hash warning and chosen to proceed.
   * Only honoured when the server enables the override.
   */
  allowDuplicate?: boolean
}

/**
 * Arguments for uploadReceipt.
 *
 * @behaviour onProgress fires with 0–100 integer percent values as XHR upload progresses.
 * @errors metadata is required; logFile is optional sidecar.
 */
export interface UploadReceiptArgs {
  caseId: string
  requestId: string
  file: File
  logFile?: File
  metadata: UploadReceiptMetadata
  onProgress?: (percent: number) => void
}

/**
 * Arguments for buildReceiptFormData, typed to accept optional logFile without exactOptionalPropertyTypes issues.
 *
 * @behaviour logFile may be undefined; if so the logFile field is omitted from the FormData.
 * @errors None — pure synchronous transformation.
 */
export interface BuildReceiptFormDataArgs {
  file: File
  logFile?: File
  metadata: UploadReceiptMetadata
}

/**
 * Build the multipart form body for a receipt upload.
 *
 * @behaviour Sends metadata as a single JSON-stringified field, file as the zip, logFile if provided.
 * @errors No validation — caller must validate before calling.
 */
export function buildReceiptFormData(args: BuildReceiptFormDataArgs): FormData {
  const formData = new FormData()
  formData.append('metadata', JSON.stringify(args.metadata))
  formData.append('file', args.file)
  if (args.logFile !== undefined) {
    formData.append('logFile', args.logFile)
  }
  return formData
}

import { API_BASE_URL as API_BASE } from './api-base-url'

/**
 * Error raised by uploadReceipt, preserving the backend's error code (e.g.
 * `evidence_not_received`, `TARGET_ALREADY_RECEIVED`) alongside the message.
 *
 * @behaviour `code` is null for network/parse failures where no structured
 *            error body was returned.
 * @errors None -- this is the error type itself.
 */
export class UploadReceiptError extends Error {
  readonly code: string | null

  constructor(message: string, code: string | null) {
    super(message)
    this.name = 'UploadReceiptError'
    this.code = code
  }
}

/**
 * Build an UploadReceiptError from a failed XHR response.
 *
 * @behaviour Parses the ErrorResponse body ({ error: code, message }); falls
 *            back to the XHR status text when the body is not parseable JSON.
 * @errors Never throws -- always returns an UploadReceiptError.
 */
function buildUploadError(xhr: XMLHttpRequest): UploadReceiptError {
  const fallbackMessage = `Upload failed: ${xhr.statusText}`
  try {
    const body = JSON.parse(xhr.responseText) as { error?: string; message?: string }
    return new UploadReceiptError(body.message ?? fallbackMessage, body.error ?? null)
  } catch {
    return new UploadReceiptError(fallbackMessage, null)
  }
}

/**
 * Upload a collection receipt using XHR so upload progress events are available.
 *
 * @behaviour Wraps XHR in a Promise; fires onProgress with integer 0–100 as bytes transfer.
 * @errors Rejects with UploadReceiptError on network failure or non-2xx response, preserving
 *         the backend's error code (e.g. `evidence_not_received`, `TARGET_ALREADY_RECEIVED`).
 */
export function uploadReceipt(args: UploadReceiptArgs): Promise<CollectionReceipt> {
  const { caseId, requestId, file, logFile, metadata, onProgress } = args
  const buildArgs: BuildReceiptFormDataArgs =
    logFile !== undefined ? { file, logFile, metadata } : { file, metadata }
  const formData = buildReceiptFormData(buildArgs)
  const url = `${API_BASE}/cases/${caseId}/collection-requests/${requestId}/receipts/upload`
  const token = tokenStorage.getAccessToken()

  return new Promise<CollectionReceipt>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    }

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const PERCENT_MAX = 100
          onProgress(Math.round((event.loaded / event.total) * PERCENT_MAX))
        }
      })
    }

    const HTTP_OK_MIN = 200
    const HTTP_OK_MAX = 300

    xhr.addEventListener('load', () => {
      if (xhr.status >= HTTP_OK_MIN && xhr.status < HTTP_OK_MAX) {
        const parsed = JSON.parse(xhr.responseText) as CollectionReceipt
        resolve(parsed)
        return
      }
      reject(buildUploadError(xhr))
    })

    xhr.addEventListener('error', () => {
      reject(new UploadReceiptError('Network error during upload', null))
    })

    xhr.addEventListener('abort', () => {
      reject(new UploadReceiptError('Upload aborted', null))
    })

    xhr.send(formData)
  })
}

/**
 * Get a single receipt
 */
export async function getReceipt(caseId: string, receiptId: string): Promise<CollectionReceipt> {
  return apiClient.get<CollectionReceipt>(`/cases/${caseId}/collection-receipts/${receiptId}`)
}

/**
 * Verify hash for a receipt
 */
export async function verifyHash(
  caseId: string,
  receiptId: string
): Promise<HashVerificationResult> {
  return apiClient.post<HashVerificationResult>(
    `/cases/${caseId}/collection-receipts/${receiptId}/verify-hash`
  )
}

/**
 * Update hash verification status
 */
export async function updateHashVerification(
  caseId: string,
  receiptId: string,
  input: UpdateHashVerificationInput
): Promise<CollectionReceipt> {
  return apiClient.put<CollectionReceipt>(
    `/cases/${caseId}/collection-receipts/${receiptId}/hash-verification`,
    input
  )
}

/**
 * Update ingestion status
 */
export async function updateIngestionStatus(
  caseId: string,
  receiptId: string,
  input: UpdateIngestionStatusInput
): Promise<CollectionReceipt> {
  return apiClient.put<CollectionReceipt>(
    `/cases/${caseId}/collection-receipts/${receiptId}/ingestion-status`,
    input
  )
}

/**
 * Update receipt notes
 */
export async function updateReceiptNotes(
  caseId: string,
  receiptId: string,
  input: UpdateReceiptNotesInput
): Promise<CollectionReceipt> {
  return apiClient.put<CollectionReceipt>(
    `/cases/${caseId}/collection-receipts/${receiptId}/notes`,
    input
  )
}

// =============================================================================
// CHAIN OF CUSTODY API
// =============================================================================

/**
 * List chain of custody entries for a receipt
 */
export async function listChainOfCustody(
  caseId: string,
  receiptId: string,
  params?: ListChainOfCustodyParams
): Promise<ChainOfCustodyListResponse> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<ChainOfCustodyListResponse>(
    `/cases/${caseId}/collection-receipts/${receiptId}/chain-of-custody${query}`
  )
}

/**
 * Record a chain of custody entry
 */
export async function recordChainOfCustody(
  caseId: string,
  receiptId: string,
  input: RecordChainOfCustodyInput
): Promise<ChainOfCustodyEntry> {
  return apiClient.post<ChainOfCustodyEntry>(
    `/cases/${caseId}/collection-receipts/${receiptId}/chain-of-custody`,
    input
  )
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const value = parseFloat((bytes / Math.pow(k, i)).toFixed(2))
  const unit = sizes[i] ?? 'Bytes'
  return `${value.toString()} ${unit}`
}

/**
 * Truncate hash for display
 */
export function truncateHash(hash: string, length = 12): string {
  if (hash.length <= length) return hash
  return `${hash.slice(0, length / 2)}...${hash.slice(-length / 2)}`
}
